import { useState, useEffect } from 'react';

const DuplicateBroadcastModal = ({
  isOpen,
  onClose,
  originalBroadcast,
  onDuplicate,
  smtpProviders = [],
  categories = [],
}) => {
  const [formData, setFormData] = useState({
    newBroadcastName: '',
    newBroadcastDescription: '',
    smtp_provider_id: '',
    category_id: '',
    warmup_params: {
      initial_volume: 10,
      max_volume: 100,
      growth_rate: 1.2,
      plateau_days: 3,
    },
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (originalBroadcast) {
      setFormData(prev => ({
        ...prev,
        newBroadcastName: `${originalBroadcast.name} (Copie)`,
        newBroadcastDescription: originalBroadcast.description,
        smtp_provider_id: originalBroadcast.smtp_provider_id,
        category_id: originalBroadcast.category_id,
      }));
    }
  }, [originalBroadcast]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWarmupParamChange = (param, value) => {
    setFormData(prev => ({
      ...prev,
      warmup_params: {
        ...prev.warmup_params,
        [param]: Number(value),
      },
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await onDuplicate(originalBroadcast.id, formData);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Dupliquer le broadcast
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nom du broadcast */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du nouveau broadcast
            </label>
            <input
              type="text"
              value={formData.newBroadcastName}
              onChange={e =>
                handleInputChange('newBroadcastName', e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.newBroadcastDescription}
              onChange={e =>
                handleInputChange('newBroadcastDescription', e.target.value)
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* SMTP Provider et Catégorie */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fournisseur SMTP
              </label>
              <select
                value={formData.smtp_provider_id}
                onChange={e =>
                  handleInputChange('smtp_provider_id', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un fournisseur</option>
                {smtpProviders.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie
              </label>
              <select
                value={formData.category_id || ''}
                onChange={e => handleInputChange('category_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Aucune catégorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Paramètres de warmup */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900">
              Configuration du warmup
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume initial (emails/jour)
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={formData.warmup_params.initial_volume}
                  onChange={e =>
                    handleWarmupParamChange('initial_volume', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume maximum (emails/jour)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={formData.warmup_params.max_volume}
                  onChange={e =>
                    handleWarmupParamChange('max_volume', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taux de croissance
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  max="3"
                  value={formData.warmup_params.growth_rate}
                  onChange={e =>
                    handleWarmupParamChange('growth_rate', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jours de plateau
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.warmup_params.plateau_days}
                  onChange={e =>
                    handleWarmupParamChange('plateau_days', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Duplication...' : 'Dupliquer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuplicateBroadcastModal;
